export type AppEvent = {
  'reservation-flow.open': {
    url?: string
    checkout?: string
    product?: number
    voucher_code?: string // @deprecated since 8-4-2024
    voucher_codes?: string[]
    arrival_date?: string
    departure_date?: string
    bath_card_id?: number
    persons_count?: number
    persons?: any[]
    nights?: number
    access_token?: string
    checkout_type?: string
    lang?: string
    ga_origin?: string
    _gl?: string | null
  }
  'reservation-flow.close': unknown
  'reservation-flow.ready': unknown
  'reservation-flow.linkercode': {
    _gl?: string | null
  }
  'reservation-flow.authenticated': {
    access_token?: string
  }
  'my-thermen.open': unknown
  'my-thermen.close': unknown
  'my-thermen.authenticate': {
    access_token?: string
  }
  'my-thermen.authenticated': { // unused?
    access_token?: string
  }
  'pre-reservation-flow.open': {
    workspace?: string
    voucherPopup?: boolean
    origin?: string
    persons_count?: number
    departure_date?: string
    arrival_date?: string
    screen?: string
    resort?: string
  }
  'pre-reservation-flow.close': unknown
  'pre-reservation-flow.ready': unknown
  'redirect': {
    tag?: string
    url: string
  }
};

export type AppSource = 'website' | 'reservation-flow' | 'my-thermen' | 'pre-reservation-flow';

/**
 * Transforms each AppEvent into a callback funciton.
 */
export type AppEvents = {
  [K in keyof AppEvent]?: (data: AppEvent[K]) => void
};

/**
 * Custom event message stucture
 */
export type AppEventMessage<T extends keyof AppEvents> = {
  event: T
  source: string
  contents: AppEvent[T]
}

/**
 * When application is intergrated via an iframe
 */
export const isIntergrated = window.self !== window.top;

/**
 * Messages event handler.
 */
const handleIncomingMessage = (source: AppSource, events: AppEvents = {}) => (message: MessageEvent) => {
  if (!message.data) return;
  if (typeof message.data.event !== 'string') return;
  if (typeof message.data.source !== 'string') return;

  // Prevent receiving messages from you self.
  if (message.data.source === source) return;

  // Handle events directly bind the the receiveMessage function.
  const handler = events[message.data.event as keyof AppEvents];
  if (handler) handler(message.data.contents);

  // Handle events by dispatching custom events, these can be defined anywhere.
  const customEvent = new CustomEvent(message.data.event, { detail: message.data });
  window.dispatchEvent(customEvent);
};

let handle: ReturnType<typeof handleIncomingMessage> | undefined;

/**
 * Register an typesafe event listener
 */
export function addEventListener<T extends keyof AppEvents>(
  event: T,
  callback: (event: CustomEvent<AppEventMessage<T>>) => void,
  options?: boolean | AddEventListenerOptions) {
  // @ts-ignore
  window.addEventListener(event, callback, options);
}

/**
 * Send / dispatch a custom event
 */
export function sendEvent<T extends keyof AppEvents>(source: AppSource, event: T, contents: AppEvent[T] = {} as AppEvent[T]) {
  event = handleLegacyEventName(event);
  window.dispatchEvent(new CustomEvent(event, { detail: { event, source, contents } }));
}

/**
 * For backwards compatibility, remove at a later moment when we are sure that
 * all users have the latest update (30-01-2026)
 */
function handleLegacyEventName<T extends keyof AppEvents>(event: T | string) {
  return event
    .replace('reservationchoice.', 'pre-reservation-flow.')
    .replace('reservationflow.', 'reservation-flow.')
    .replace('mijnthermen.', 'my-thermen.') as T;
}

/**
 * Start receiving messages from other applications.
 * @param events
 */
export function receiveMessages(source: AppSource, events: AppEvents = {}) {
  handle = handleIncomingMessage(source, events);
  window.addEventListener('message', handle);
}

/**
 * Stop receiving messages
 */
export function stopMessages() {
  if (handle) window.removeEventListener('message', handle);
}

/**
 * Send a message
 */
export function sendMessage<T extends keyof AppEvents>(source: AppSource, event: T, contents: AppEvent[T] = {} as AppEvent[T]) {
  event = handleLegacyEventName(event);
  window.parent.postMessage({ event, source, contents }, '*');
}

/**
 * Send a message to an iframe
 */
export function sendIframeMessage<T extends keyof AppEvents>(iframe: HTMLIFrameElement, source: AppSource, event: T, contents: AppEvent[T] = {} as AppEvent[T]) {
  iframe.contentWindow?.postMessage({ event, source, contents }, '*');
}

export default {
  addEventListener,
  isIntergrated,
  receiveMessages,
  sendEvent,
  sendIframeMessage,
  sendMessage,
  stopMessages,
};
